import React, { PropsWithChildren, useEffect, useMemo, useState } from 'react';
import SVG from 'react-inlinesvg';
import cx from 'classnames';
import { isArray, isNumber } from 'lodash';
import excludeSvg from 'assets/icons/exclude.svg';
import arrowLightDown from 'assets/icons/arrow_light_down.svg';
import Chip from 'lib/Chip';
import { Color, TextPriority } from 'lib/Chip/Chip.types';
import { ButtonThemes, SectionButtonProps, SectionButtonRounded } from './SectionButton.types';
import SectionButtonMenu from './SectionButtonMenu';
import SectionButtonChipList from './SectionButtonChipList';

const SectionButton = ({
  lineId,
  theme,
  chipColor = Color.PRIMARY,
  chipTextPriority = TextPriority.LABEL,
  dataTestId,
  icon,
  iconStyles,
  label,
  children,
  isActive,
  isDisabled,
  isRemoveChipDisabled,
  isEllipsisDisabled,
  isRequired,
  selected,
  onClick,
  onClear,
  onDuplicate,
  areChipsAutoCollapsed = false,
  rounded = SectionButtonRounded.DEFAULT,
  sectionGroupStatus,
  dataPendoId,
  hasIndicator = false,
  clearButtonLabel,
  enableAccordionToggle = false,
  extendedChildren,
  isShowLessToggleDisabled,
  collapsedExcludedNumber,
  collapsedIncludedNumber,
  isClearAllDisabled = false,
  className,
  customMenuContent,
  truncateChipText = true,
  stopPropagation = false,
  onShowMoreMenuClick,
  onRemoveChip,
  suppressDisabledStyle = false,
  extraMenuActions,
  // eslint-disable-next-line sonarjs/cognitive-complexity
}: PropsWithChildren<SectionButtonProps>): React.ReactElement => {
  const showDisabledStyle = isDisabled && !suppressDisabledStyle;
  const [areChipsCollapsed, setAreChipsCollapsed] = useState(areChipsAutoCollapsed);
  const [isContentHidden, setIsContentHidden] = useState(false);

  const chipsLength = useMemo(():
    | number
    | {
        included: number;
        excluded: number;
      } => {
    if (!selected) return 0;

    if (isArray(selected)) return selected.length;

    let nonCategorisedCount = 0;
    const includeExcludeCount = { included: 0, excluded: 0 };

    Object.keys(selected).forEach((category) => {
      const chips = selected[category];

      if (isArray(chips)) nonCategorisedCount += chips.length;
      else {
        const { included, excluded } = chips;

        if (included) includeExcludeCount.included += included.length;
        if (excluded) includeExcludeCount.excluded += excluded.length;
      }
    });

    return nonCategorisedCount !== 0 ? nonCategorisedCount : includeExcludeCount;
  }, [selected]);

  useEffect(() => {
    setAreChipsCollapsed(areChipsAutoCollapsed);
  }, [areChipsAutoCollapsed]);

  useEffect(() => {
    if (chipsLength === 0 && !collapsedExcludedNumber && !collapsedIncludedNumber) {
      setAreChipsCollapsed(false);
    }
  }, [chipsLength, collapsedExcludedNumber, collapsedIncludedNumber]);

  const buttonContent = useMemo(() => {
    if (isContentHidden) return null;

    if (areChipsCollapsed && (collapsedExcludedNumber || collapsedIncludedNumber))
      return (
        <div className="flex align-middle items-end gap-1">
          {!!collapsedIncludedNumber && (
            <Chip
              key={`${lineId}-${label}-collapsed-included-number-chip`}
              label={`${collapsedIncludedNumber}`}
              color={chipColor}
              onRemove={onRemoveChip ? () => onRemoveChip(undefined, true) : undefined}
              isDisabled={isDisabled || isRemoveChipDisabled}
            />
          )}
          {!!collapsedExcludedNumber && (
            <Chip
              key={`${lineId}-${label}-collapsed-excluded-number-chip`}
              label={`${collapsedExcludedNumber}`}
              color={Color.GREY}
              icon={excludeSvg}
              onRemove={onRemoveChip ? () => onRemoveChip(undefined, false) : undefined}
              isDisabled={isDisabled}
            />
          )}
        </div>
      );

    if (isNumber(chipsLength) && chipsLength > 0 && areChipsCollapsed)
      return (
        <Chip
          key={`${lineId}-${label}-collapsed-chips-number-chip`}
          label={chipsLength.toString()}
          color={chipColor}
          onRemove={onRemoveChip ? () => onRemoveChip() : undefined}
          isDisabled={isDisabled || isRemoveChipDisabled}
        />
      );

    if (!isNumber(chipsLength) && areChipsCollapsed)
      return (
        <div className="flex align-middle items-end gap-1">
          {!!chipsLength.included && (
            <Chip
              key={`${lineId}-${label}-collapsed-included-number-chip`}
              label={`${chipsLength.included}`}
              color={chipColor}
              onRemove={onRemoveChip ? () => onRemoveChip(undefined, true) : undefined}
              isDisabled={isDisabled || isRemoveChipDisabled}
            />
          )}
          {!!chipsLength.excluded && (
            <Chip
              key={`${lineId}-${label}-collapsed-excluded-number-chip`}
              label={`${chipsLength.excluded}`}
              color={Color.GREY}
              icon={excludeSvg}
              onRemove={onRemoveChip ? () => onRemoveChip(undefined, false) : undefined}
              isDisabled={isDisabled || isRemoveChipDisabled}
            />
          )}
        </div>
      );

    if (selected)
      return (
        <SectionButtonChipList
          lineId={lineId}
          selected={selected}
          hasIndicator={hasIndicator}
          chipTextPriority={chipTextPriority}
          chipColor={showDisabledStyle ? Color.DISABLED : chipColor}
          truncateChipText={truncateChipText}
          onRemove={onRemoveChip ? (value, included) => onRemoveChip(value, included) : undefined}
          isDisabled={isDisabled || isRemoveChipDisabled}
        />
      );

    return children;
  }, [
    selected,
    isDisabled,
    isRemoveChipDisabled,
    chipsLength,
    areChipsCollapsed,
    isContentHidden,
    collapsedExcludedNumber,
    collapsedIncludedNumber,
    chipTextPriority,
    truncateChipText,
    children,
    hasIndicator,
    chipColor,
    onRemoveChip,
  ]);

  const hideContentButton = (): JSX.Element => {
    const onHideContentClick = (
      e: React.MouseEvent<HTMLDivElement, MouseEvent> | React.KeyboardEvent<HTMLDivElement>,
    ): void => {
      e.stopPropagation();
      setIsContentHidden(!isContentHidden);
    };

    return (
      <div role="presentation" onClick={onHideContentClick} className="flex items-center w-6 h-6">
        <SVG
          className={cx('fill-current w-4 h-4 text-neutral-900', { 'rotate-180': !isContentHidden })}
          src={arrowLightDown}
        />
      </div>
    );
  };

  return (
    <div
      className={cx(
        'flex flex-col p-1 border',
        rounded,
        {
          'ring-1 border-neutral-950 ring-primary-600 bg-primary-50': isActive,
          [ButtonThemes[theme].inactive]: !isActive && !showDisabledStyle,
          [ButtonThemes[theme].buttonContainer]: !showDisabledStyle,
          [ButtonThemes[theme].disabled]: showDisabledStyle,
        },
        className,
      )}
    >
      <button
        data-test-id={dataTestId}
        data-pendo-id={dataPendoId || null}
        type="button"
        className="relative flex w-full gap-1 disabled:cursor-not-allowed"
        disabled={isDisabled}
        onClick={(e) => {
          if (stopPropagation) {
            e.stopPropagation();
          }
          onClick?.();
        }}
      >
        <div className="flex flex-row items-center self-start gap-1 py-0.5">
          {icon ? (
            <SVG
              className={cx('fill-current w-4 h-4', iconStyles, {
                'text-neutral-700': showDisabledStyle,
                [ButtonThemes[theme].iconColor]: !showDisabledStyle,
              })}
              src={icon}
            />
          ) : null}
          <span
            className={cx('sub-header-base whitespace-nowrap', {
              'text-neutral-700': showDisabledStyle,
              [ButtonThemes[theme].textColor]: !showDisabledStyle,
            })}
          >
            {label}
          </span>
          {sectionGroupStatus ? (
            <div
              data-test-id="section-group-status-indicator"
              className={cx(sectionGroupStatus, 'w-1.5 h-1.5 mr-2 mx-1.5 rounded-full shrink-0')}
            />
          ) : null}
        </div>
        <div className="flex flex-1 justify-end">{buttonContent}</div>
        {!selected && isRequired ? (
          <div className="flex self-center justify-end">
            <span className={showDisabledStyle ? 'text-neutral-700' : 'text-primary'}>Required</span>
          </div>
        ) : null}
        {onClear ? (
          <SectionButtonMenu
            onClear={onClear}
            clearButtonLabel={clearButtonLabel}
            onToggle={() => setAreChipsCollapsed(!areChipsCollapsed)}
            onDuplicate={onDuplicate}
            isEllipsisDisabled={isDisabled || isEllipsisDisabled}
            isToggleDisabled={
              isShowLessToggleDisabled ||
              (isShowLessToggleDisabled === undefined && isNumber(chipsLength) && chipsLength <= 1) ||
              (!isNumber(chipsLength) && chipsLength.included <= 1 && chipsLength.excluded <= 1)
            }
            isCollapsed={areChipsCollapsed}
            isClearAllDisabled={isClearAllDisabled}
            onShowMoreMenuClick={onShowMoreMenuClick}
            extraMenuActions={extraMenuActions}
          />
        ) : null}
        {customMenuContent ?? null}
        {enableAccordionToggle ? hideContentButton() : null}
      </button>
      {!isContentHidden ? extendedChildren : null}
    </div>
  );
};

export default SectionButton;
